import React, { useState, useCallback, useMemo, useEffect } from 'react';
import '../css/Tarjetas.css';
import { ReactComponent as Eliminar } from '../assets/icon/eliminar.svg';
import { ReactComponent as Editar } from '../assets/icon/editar.svg';
import { ReactComponent as Agregar } from '../assets/icon/agregartarjeta.svg';
import { ReactComponent as Recargar } from '../assets/icon/recargar.svg';
import { ReactComponent as Importar } from '../assets/icon/subirarchivo.svg';
import { useNavigate } from "react-router-dom";
import TablaTarjetas from '../Tarjetas/TablaTarjetas';
import { useTarjetas, deleteTarjeta } from '../services/tarjetas';
import { useToast } from '../contexts/ToastContext';
import BulkDeleteModal from '../Components/BulkDeleteModal';

function Tarjetas() {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItems, setDeletingItems] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWithClient, setFilterWithClient] = useState(false);
  const { showError, showSuccess } = useToast();

  useEffect(() => {
    const savedResult = localStorage.getItem('tarjetaSearchResult');
    const savedTerm = localStorage.getItem('tarjetaSearchTerm');
    
    if (savedResult) {
      try {
        const parsed = JSON.parse(savedResult);
        setSearchResult(parsed);
        setSearchTerm(savedTerm || '');
      } catch (e) {
        console.error('Error parsing search result:', e);
      }
      localStorage.removeItem('tarjetaSearchResult');
      localStorage.removeItem('tarjetaSearchTerm');
    }
  }, []);

  const { data: tarjetas, loading, error, refetch } = useTarjetas({
    immediate: true
  });

  const baseData = searchResult !== null ? searchResult : tarjetas;
  
  const displayData = useMemo(() => {
    if (!baseData) return [];
    if (!filterWithClient) return baseData;
    return baseData.filter(tarjeta => {
      const hasClient = tarjeta.NombreCompleto || 
        (tarjeta.Nombres || tarjeta.Apellidos) ||
        tarjeta.NombreCliente;
      return hasClient;
    });
  }, [baseData, filterWithClient]);

  useEffect(() => {
    if (error) {
      showError(error, 'Error de conexión');
    }
  }, [error, showError]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    
    const itemsToDelete = tarjetas.filter(t => selectedIds.includes(t.NumeroTarjeta));
    setDeletingItems(itemsToDelete);
    setShowDeleteModal(true);
  }, [selectedIds, tarjetas]);

  const handleConfirmDelete = useCallback(async (numeroTarjeta, item) => {
    const result = await deleteTarjeta(numeroTarjeta);
    if (result.success && result.error === 0) {
      return { success: true };
    }
    throw new Error(result.mensaje || 'Error al eliminar');
  }, []);

  const handleDeleteComplete = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedIds([]);
    refetch();
    showSuccess('Tarjeta(s) eliminada(s) correctamente');
  }, [refetch, showSuccess]);

  const handleEditSelected = useCallback(() => {
    if (selectedIds.length === 1) {
      navigate(`/dashboard/tarjetas/editartarjeta/${encodeURIComponent(selectedIds[0])}`);
    }
  }, [selectedIds, navigate]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const buttonStates = useMemo(() => {
    const hasNoSelection = selectedIds.length === 0;
    const hasSingleSelection = selectedIds.length === 1;
    const hasMultipleSelection = selectedIds.length > 1;
    const hasData = displayData && displayData.length > 0;

    return {
      nuevo: hasNoSelection && hasData,
      editar: hasSingleSelection && hasData,
      eliminar: (hasSingleSelection || hasMultipleSelection) && hasData
    };
  }, [selectedIds, displayData]);

  const handleRefresh = useCallback(() => {
    setSearchResult(null);
    setSearchTerm('');
    setFilterWithClient(false);
    refetch();
  }, [refetch]);

  const handleClearSearch = useCallback(() => {
    setSearchResult(null);
    setSearchTerm('');
    setFilterWithClient(false);
  }, []);

  return (
    <div className="bodyTarjetas">

      {/* TÍTULO */}
      <div className='tituloTarjetas'><p>Tarjetas</p></div>

      {/* CONTENIDO SUPERIOR */}
      <div className='contenidoSuperiorTarjetas'>
        <div className='botonesTarjetas'>
          
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={filterWithClient}
              onChange={(e) => {
                setFilterWithClient(e.target.checked);
                setCurrentPage(1);
              }}
            />
            Solo tarjetas con cliente
          </label>
          
          {searchResult !== null && (
            <div className="search-result-info">
              <span>Resultados de búsqueda: "{searchTerm}" ({displayData.length} encontrado{displayData.length !== 1 ? 's' : ''})</span>
              <button 
                className='botonesEdicionTarjetas'
                onClick={handleClearSearch}
                title="Ver todas las tarjetas"
              >
                Ver todas
              </button>
            </div>
          )}
                
          <button 
            className='botonesEdicionTarjetas'
            onClick={() => navigate("/dashboard/tarjetas/agregartarjeta")}
            title="Agregar nueva tarjeta"
          >
            <div className="svgBotonesTarjetas"><Agregar className="logo-svgBotones" /></div>
            Agregar Tarjeta
          </button>

          <button 
            className='botonesEdicionTarjetas'
            onClick={() => navigate("/dashboard/tarjetas/importar")}
            title="Importar tarjetas desde Excel"
          >
            <div className="svgBotonesTarjetas"><Importar className="logo-svgBotones" /></div>
            Importar
          </button>
 
          <button
            className='botonesEdicionTarjetas'
            disabled={!buttonStates.eliminar}
            onClick={handleDeleteSelected}
            title={buttonStates.eliminar ? `Eliminar ${selectedIds.length} tarjeta(s)` : "Selecciona tarjetas para eliminar"}
          >
            <div className="svgBotonesTarjetas"><Eliminar className="logo-svgBotones" /></div>
            Eliminar Tarjeta{selectedIds.length > 1 ? 's' : ''}
          </button>

          <button 
            className='botonesEdicionTarjetas'
            onClick={handleRefresh}
            disabled={loading}
            title="Recargar tarjetas"
          >
            <div className="svgBotonesTarjetas">
              <Recargar className={`logo-svgBotones ${loading ? 'rotating' : ''}`} />
            </div>
            Recargar
          </button>
        </div>
      </div>

      {/* AQUÍ SE MOSTRARÁ LA TABLA */}
      <div className="tablaContainerTarjetas">
        <TablaTarjetas 
          tarjetas={displayData}
          loading={loading}
          error={error}
          onRetry={refetch}
          emptyStateNoReload={true}
          pageSize={20}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSelectionChange={(selectedIdsList) => {
            setSelectedIds(selectedIdsList || []);
            if (currentPage !== 1) {
              setCurrentPage(1);
            }
          }}
        />
      </div>

      <BulkDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar Tarjetas"
        items={deletingItems}
        onDelete={handleConfirmDelete}
        getItemId={(item) => item.NumeroTarjeta}
        getItemLabel={(item) => `****${item.NumeroTarjeta?.slice(-4)}`}
        entityName="tarjeta"
      />

    </div>
  );
}

export default Tarjetas;
